/* ============================================================
   ZuhauseTEST – Upselling im Warenkorb
   ============================================================

   Gehört zu snippets/zt-cart-upsell.liquid.

   - Legt die gewählte Variante per /cart/add.js in den Warenkorb
   - Drawer: rendert cart-drawer + cart-icon-bubble über die
     Section Rendering API neu (gleicher Weg wie das Produktformular)
   - Warenkorbseite: lädt die Seite neu
   - Meldet cartUpdate über pubsub (z. B. für das Gratisgeschenk)
   - Markiert den Artikel mit der Eigenschaft _upsell (Auswertung
     in den Bestellungen)
   ============================================================ */

if (!customElements.get('zt-cart-upsell')) {
  // Stylesheet einmal dauerhaft in den <head>: Der Drawer ersetzt beim Neurendern
  // sein HTML samt <link>, ohne diese Kopie wäre der Block kurz ungestylt.
  const stylesheet = document.querySelector('link[href*="zt-cart-upsell.css"]');
  if (stylesheet && !document.head.contains(stylesheet)) {
    document.head.appendChild(stylesheet.cloneNode());
  }

  class ZtCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.updateNav = this.updateNav.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-zt-upsell-track]');
      this.prev = this.querySelector('[data-zt-upsell-prev]');
      this.next = this.querySelector('[data-zt-upsell-next]');
      if (!this.track || !this.prev) return;

      this.track.addEventListener('scroll', this.updateNav, { passive: true });
      // Der Drawer ist beim Rendern oft noch zu – Breite erst messen, wenn sie da ist
      this.resizeObserver = new ResizeObserver(this.updateNav);
      this.resizeObserver.observe(this.track);
      this.updateNav();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    updateNav() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const overflow = scrollWidth - clientWidth > 1;
      this.querySelector('.zt-cart-upsell__nav').hidden = !overflow;
      this.prev.disabled = scrollLeft <= 1;
      this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
    }

    scrollByItem(direction) {
      const item = this.track.querySelector('.zt-cart-upsell__item');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' });
    }

    onChange(event) {
      const select = event.target.closest('[data-zt-upsell-variant]');
      if (!select) return;
      const button = select.closest('.zt-cart-upsell__item').querySelector('[data-zt-upsell-add]');
      button.dataset.variantId = select.value;
    }

    onClick(event) {
      if (event.target.closest('[data-zt-upsell-prev]')) return this.scrollByItem(-1);
      if (event.target.closest('[data-zt-upsell-next]')) return this.scrollByItem(1);

      const button = event.target.closest('[data-zt-upsell-add]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      this.add(button);
    }

    async add(button) {
      const variantId = Number(button.dataset.variantId);
      const cartDrawer = document.querySelector('cart-drawer');
      const useDrawer = this.dataset.context === 'drawer' && cartDrawer;

      this.setError('');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        const response = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            // Versteckte Eigenschaft (Unterstrich): erscheint nicht im Shop, aber
            // in der Bestellung – so lassen sich Upsell-Käufe auswerten
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { _upsell: this.dataset.context === 'page' ? 'Warenkorbseite' : 'Cart-Drawer' },
              },
            ],
            sections: useDrawer ? ['cart-drawer', 'cart-icon-bubble'] : [],
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();

        if (!response.ok || state.status) {
          this.setError(state.description || state.message || window.cartStrings?.error);
          return;
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zt-cart-upsell', cartData: state, variantId });
        }

        if (useDrawer) {
          cartDrawer.renderContents(state);
        } else {
          window.location.reload();
        }
      } catch (e) {
        console.error(e);
        this.setError(window.cartStrings?.error);
      } finally {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      }
    }

    setError(message) {
      const error = this.querySelector('.zt-cart-upsell__error');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }
  }

  customElements.define('zt-cart-upsell', ZtCartUpsell);
}
